import { TriangleAlert } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApprovalCancelled, useApproval } from "@/app/approval";
import { usePos } from "@/app/context";
import { Modal } from "@/components/Modal";
import { MoneyText } from "@/components/MoneyText";
import { call } from "@/lib/api";
import { errorText, isApiError } from "@/lib/errors";
import { newIdempotencyKey } from "@/lib/idempotency";
import type { ReturnResult, SaleLookup } from "@/lib/types";

const VOID_REASONS = ["wrong_entry", "duplicate", "customer", "other"] as const;

/**
 * 取消 (void) of a sale of the open shift (design/05 §3.6): two steps — the reason, then 「本当に
 * 取り消す」 — and the manager's PIN (the server asks: `pos.void`). Every payment goes back the
 * way it came; a card-terminal payment must be cancelled on the terminal too.
 */
export function VoidDialog({
  sale,
  onClose,
  onVoided,
}: {
  sale: SaleLookup;
  onClose: () => void;
  onVoided: (result: ReturnResult) => void;
}) {
  const { t, i18n } = useTranslation();
  const { api } = usePos();
  const { withApproval } = useApproval();
  const [step, setStep] = useState<1 | 2>(1);
  const [reason, setReason] = useState<string>("wrong_entry");
  const [note, setNote] = useState("");
  const [refs, setRefs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = useRef(newIdempotencyKey());
  const cards = sale.payments.filter((p) => p.method === "edc_external" && p.paymentId);

  const submit = async () => {
    setBusy(true);
    setError(null);
    const ja = i18n.getFixedT("ja");
    const text = `${ja(`returns.void_reason_${reason}`)}${note.trim() ? ` — ${note.trim()}` : ""}`;
    const edcRefs = Object.fromEntries(
      Object.entries(refs)
        .filter(([, v]) => v.trim())
        .map(([k, v]) => [k, v.trim()]),
    );
    try {
      const result = await withApproval(
        (token) =>
          call(() =>
            api.POST("/v1/pos/orders/{sale_id}/void", {
              params: { path: { sale_id: sale.saleId } },
              body: { reason: text.slice(0, 200), edcRefs },
              headers: {
                "Idempotency-Key": key.current,
                ...(token ? { "X-Approval-Token": token } : {}),
              },
            }),
          ),
        { subject: sale.saleId, amount: sale.total },
      );
      onVoided(result);
    } catch (err) {
      if (!isApiError(err, "network") && !(isApiError(err) && err.status >= 500)) {
        key.current = newIdempotencyKey();
      }
      if (err instanceof ApprovalCancelled) {
        onClose(); // cancel = nothing changed
        return;
      }
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title={t("returns.void_title")}
      description={step === 1 ? t("returns.void_help") : t("returns.void_confirm_help")}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button
            type="button"
            onClick={step === 1 ? onClose : () => setStep(1)}
            disabled={busy}
            className="min-h-pos-touch rounded-md border px-6 text-lg"
          >
            {step === 1 ? t("app.cancel") : t("app.back")}
          </button>
          {step === 1 ? (
            <button
              type="button"
              onClick={() => setStep(2)}
              disabled={reason === "other" && !note.trim()}
              className="min-h-pos-touch rounded-md bg-primary px-6 text-lg font-semibold text-primary-foreground disabled:opacity-50"
            >
              {t("returns.void_next")}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void submit()}
              disabled={busy}
              className="min-h-pos-touch rounded-md bg-status-problem px-6 text-lg font-semibold text-white disabled:opacity-50"
            >
              {busy ? t("app.saving") : t("returns.void_submit")}
            </button>
          )}
        </>
      }
    >
      {step === 1 ? (
        <div className="flex flex-col gap-3">
          <fieldset className="grid grid-cols-2 gap-2">
            <legend className="mb-1 font-medium">{t("returns.void_reason")}</legend>
            {VOID_REASONS.map((code) => (
              <button
                key={code}
                type="button"
                aria-pressed={reason === code}
                onClick={() => setReason(code)}
                className="min-h-pos-touch rounded-md border px-3 text-lg aria-pressed:border-primary aria-pressed:bg-accent"
              >
                {t(`returns.void_reason_${code}`)}
              </button>
            ))}
          </fieldset>
          <label className="flex flex-col gap-1">
            <span className="text-sm">{t("returns.note")}</span>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={150}
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="flex items-center gap-2 rounded-md border-2 border-status-problem p-3 text-lg">
            <TriangleAlert aria-hidden className="size-6 shrink-0 text-pos-problem" />
            {t("returns.void_everything", { no: sale.receiptNo ?? sale.orderNumber })}
            <MoneyText amount={sale.total} size="lg" className="ml-auto" />
          </p>
          {cards.map((p) => (
            <label key={p.paymentId} className="flex flex-col gap-1">
              <span className="text-sm">
                {t("returns.void_edc", {
                  brand: t(`brand.${p.cardBrand ?? ""}`, { defaultValue: p.cardBrand ?? "" }),
                })}
              </span>
              <input
                value={refs[p.paymentId ?? ""] ?? ""}
                onChange={(e) =>
                  setRefs({ ...refs, [p.paymentId ?? ""]: e.target.value.slice(0, 40) })
                }
                autoComplete="off"
                inputMode="numeric"
                className="min-h-12 rounded-md border px-3 font-mono text-lg"
              />
            </label>
          ))}
          {error ? (
            <p role="alert" className="text-pos-problem">
              {error}
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
